import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { db, DailyNutritionSummary } from '../../lib/database';
import { useNoshStore } from '../../lib/store';
import { calculateNutritionTargets, type DietaryPreference } from '../../lib/nutrition-calculator';

interface DailyNutritionProgressProps {
  className?: string;
  animationDelay?: number;
  isMobile?: boolean;
}

export function DailyNutritionProgress({ 
  className = '', 
  animationDelay = 0,
  isMobile = false 
}: DailyNutritionProgressProps) {
  const { user, nutritionUpdateTrigger } = useNoshStore();
  // Use singleton database service
  const [nutritionSummary, setNutritionSummary] = useState<DailyNutritionSummary | null>(null);
  const [nutritionTargets, setNutritionTargets] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Extract loadNutritionData as a reusable function
  const loadNutritionData = async () => {
    try {
      setLoading(true);
      
      // Load today's nutrition summary
      const summary = await db.getDailyNutritionSummary();
      setNutritionSummary(summary);

      // Calculate nutrition targets if user has body stats
      if (user.age && user.gender && user.height && user.weight && user.activityLevel) {
        const targets = calculateNutritionTargets({
          age: user.age,
          gender: user.gender,
          height: user.height,
          weight: user.weight,
          activityLevel: user.activityLevel,
          goal: user.goal || 'maintain',
          dietaryPreferences: (user.preferences || []) as DietaryPreference[]
        });
        setNutritionTargets(targets);
      }
    } catch (error) {
      console.error('❌ Error loading nutrition data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load nutrition data only when component mounts or user profile changes
  useEffect(() => {
    loadNutritionData();
  }, [user.age, user.gender, user.height, user.weight, user.activityLevel, user.goal, user.preferences]); // Only reload when user profile data actually changes

  // Listen for nutrition update events (when meals/recipes are logged)
  useEffect(() => {
    if (nutritionUpdateTrigger > 0) {
      console.log('🔄 Nutrition update triggered, refreshing data...');
      loadNutritionData();
    }
  }, [nutritionUpdateTrigger]);

  // Removed automatic refresh on tab focus - data only refreshes when:
  // 1. Component mounts initially
  // 2. User profile data changes (age, weight, etc.)
  // 3. Nutrition update trigger fires (when meals are logged)
  // No need to refresh on tab switch since nutrition data doesn't change by itself

  // If no targets available, show placeholder
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: animationDelay }}
        className={`bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-3 ${className}`}
      >
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (!nutritionTargets || !nutritionSummary) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: animationDelay }}
        className={`bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800/30 rounded-xl p-3 ${className}`}
      >
        <div className="text-center">
          <div className="text-2xl mb-1">🎯</div>
          <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">
            Complete your body stats to track nutrition
          </p>
        </div>
      </motion.div>
    );
  }

  // Calculate progress percentages
  const calorieProgress = Math.min((nutritionSummary.totalCalories / nutritionTargets.dailyCalories) * 100, 100);
  const proteinProgress = Math.min((nutritionSummary.totalProtein / nutritionTargets.macroTargets.protein.target) * 100, 100);
  const carbProgress = Math.min((nutritionSummary.totalCarbs / nutritionTargets.macroTargets.carbs.target) * 100, 100);
  const fatProgress = Math.min((nutritionSummary.totalFat / nutritionTargets.macroTargets.fat.target) * 100, 100);
  const fiberProgress = Math.min((nutritionSummary.totalFiber / nutritionTargets.macroTargets.fiber.target) * 100, 100);

  // Unified helper function for displaying current/target values
  const formatNutritionDisplay = (current: number, target: number, unit: string = 'g') => 
    `${Math.round(current)}${unit} / ${Math.round(target)}${unit}`;

  // Determine layout based on mobile/desktop
  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: animationDelay }}
        className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 ${className}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="text-lg">📊</span>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Today's Nutrition</h3>
          </div>
          <div className="text-xs text-gray-700 dark:text-gray-300">
            {nutritionSummary.mealsCount + nutritionSummary.recipesCount} entries
          </div>
        </div>

        {/* Mobile Grid - 2x3 layout */}
        <div className="grid grid-cols-2 gap-2">
          {/* Calories - Main metric */}
          <div className="col-span-2 bg-white/50 dark:bg-gray-800/30 rounded-lg p-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-green-700 dark:text-green-300">Calories</span>
              <span className="text-xs text-green-600 dark:text-green-400">
                {nutritionSummary.totalCalories} / {nutritionTargets.dailyCalories}
              </span>
            </div>
            <div className="w-full bg-green-200 dark:bg-green-800/30 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${calorieProgress}%` }}
                transition={{ duration: 0.8, delay: animationDelay + 0.1 }}
                className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full"
              />
            </div>
          </div>

          {/* Protein */}
          <div className="bg-white/50 dark:bg-gray-800/30 rounded-lg p-2">
            <div className="flex items-center space-x-1 mb-1">
              <span className="text-xs">💪</span>
              <span className="text-xs font-medium text-green-700 dark:text-green-300">Protein</span>
            </div>
            <div className="text-sm font-bold text-green-900 dark:text-green-100 mb-1">
              {formatNutritionDisplay(nutritionSummary.totalProtein, nutritionTargets.macroTargets.protein.target)}
            </div>
            <div className="w-full bg-blue-200 dark:bg-blue-800/30 rounded-full h-1.5">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${proteinProgress}%` }}
                transition={{ duration: 0.8, delay: animationDelay + 0.2 }}
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-1.5 rounded-full"
              />
            </div>
          </div>

          {/* Carbs */}
          <div className="bg-white/50 dark:bg-gray-800/30 rounded-lg p-2">
            <div className="flex items-center space-x-1 mb-1">
              <span className="text-xs">🌾</span>
              <span className="text-xs font-medium text-green-700 dark:text-green-300">Carbs</span>
            </div>
            <div className="text-sm font-bold text-green-900 dark:text-green-100 mb-1">
              {formatNutritionDisplay(nutritionSummary.totalCarbs, nutritionTargets.macroTargets.carbs.target)}
            </div>
            <div className="w-full bg-amber-200 dark:bg-amber-800/30 rounded-full h-1.5">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${carbProgress}%` }}
                transition={{ duration: 0.8, delay: animationDelay + 0.3 }}
                className="bg-gradient-to-r from-amber-500 to-amber-600 h-1.5 rounded-full"
              />
            </div>
          </div>

          {/* Fat */}
          <div className="bg-white/50 dark:bg-gray-800/30 rounded-lg p-2">
            <div className="flex items-center space-x-1 mb-1">
              <span className="text-xs">🥑</span>
              <span className="text-xs font-medium text-green-700 dark:text-green-300">Fat</span>
            </div>
            <div className="text-sm font-bold text-green-900 dark:text-green-100 mb-1">
              {formatNutritionDisplay(nutritionSummary.totalFat, nutritionTargets.macroTargets.fat.target)}
            </div>
            <div className="w-full bg-purple-200 dark:bg-purple-800/30 rounded-full h-1.5">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${fatProgress}%` }}
                transition={{ duration: 0.8, delay: animationDelay + 0.4 }}
                className="bg-gradient-to-r from-purple-500 to-purple-600 h-1.5 rounded-full"
              />
            </div>
          </div>

          {/* Fiber */}
          <div className="bg-white/50 dark:bg-gray-800/30 rounded-lg p-2">
            <div className="flex items-center space-x-1 mb-1">
              <span className="text-xs">🥬</span>
              <span className="text-xs font-medium text-green-700 dark:text-green-300">Fiber</span>
            </div>
            <div className="text-sm font-bold text-green-900 dark:text-green-100 mb-1">
              {formatNutritionDisplay(nutritionSummary.totalFiber, nutritionTargets.macroTargets.fiber.target)}
            </div>
            <div className="w-full bg-teal-200 dark:bg-teal-800/30 rounded-full h-1.5">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${fiberProgress}%` }}
                transition={{ duration: 0.8, delay: animationDelay + 0.5 }}
                className="bg-gradient-to-r from-teal-500 to-teal-600 h-1.5 rounded-full"
              />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Desktop layout - horizontal progress bars
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: animationDelay }}
      className={`bg-transparent border-none rounded-xl p-2 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-heading font-semibold text-black dark:text-white">Today's Nutrition Progress</h3>
        </div>
        <div className="text-sm text-black dark:text-white">
          {nutritionSummary.mealsCount + nutritionSummary.recipesCount} entries logged
        </div>
      </div>

      {/* Desktop Progress Bars */}
      <div className="space-y-2">
        {/* Calories */}
        <div className="flex items-center space-x-4">
          <div className="w-20 text-sm font-medium text-green-700 dark:text-green-300">Calories</div>
          <div className="flex-1">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-green-600 dark:text-green-400">
                {nutritionSummary.totalCalories} / {nutritionTargets.dailyCalories} cal
              </span>
              <span className="text-xs text-green-500 dark:text-green-400">
                {Math.round(calorieProgress)}%
              </span>
            </div>
            <div className="w-full bg-green-200 dark:bg-green-800/30 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${calorieProgress}%` }}
                transition={{ duration: 0.8, delay: animationDelay + 0.1 }}
                className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full"
              />
            </div>
          </div>
        </div>

        {/* Macros Row */}
        <div className="grid grid-cols-4 gap-4">
          {/* Protein */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-1">
                <span className="text-sm">💪</span>
                <span className="text-sm font-medium text-green-700 dark:text-green-300">Protein</span>
              </div>
              <div className="text-base font-bold text-green-900 dark:text-green-100">
                {formatNutritionDisplay(nutritionSummary.totalProtein, nutritionTargets.macroTargets.protein.target)}
              </div>
            </div>
            <div className="w-full bg-blue-200 dark:bg-blue-800/30 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${proteinProgress}%` }}
                transition={{ duration: 0.8, delay: animationDelay + 0.2 }}
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full"
              />
            </div>
          </div>

          {/* Carbs */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-1">
                <span className="text-sm">🌾</span>
                <span className="text-sm font-medium text-green-700 dark:text-green-300">Carbs</span>
              </div>
              <div className="text-base font-bold text-green-900 dark:text-green-100">
                {formatNutritionDisplay(nutritionSummary.totalCarbs, nutritionTargets.macroTargets.carbs.target)}
              </div>
            </div>
            <div className="w-full bg-amber-200 dark:bg-amber-800/30 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${carbProgress}%` }}
                transition={{ duration: 0.8, delay: animationDelay + 0.3 }}
                className="bg-gradient-to-r from-amber-500 to-amber-600 h-2 rounded-full"
              />
            </div>
          </div>

          {/* Fat */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-1">
                <span className="text-sm">🥑</span>
                <span className="text-sm font-medium text-green-700 dark:text-green-300">Fat</span>
              </div>
              <div className="text-base font-bold text-green-900 dark:text-green-100">
                {formatNutritionDisplay(nutritionSummary.totalFat, nutritionTargets.macroTargets.fat.target)}
              </div>
            </div>
            <div className="w-full bg-purple-200 dark:bg-purple-800/30 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${fatProgress}%` }}
                transition={{ duration: 0.8, delay: animationDelay + 0.4 }}
                className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full"
              />
            </div>
          </div>

          {/* Fiber */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-1">
                <span className="text-sm">🥬</span>
                <span className="text-sm font-medium text-green-700 dark:text-green-300">Fiber</span>
              </div>
              <div className="text-base font-bold text-green-900 dark:text-green-100">
                {formatNutritionDisplay(nutritionSummary.totalFiber, nutritionTargets.macroTargets.fiber.target)}
              </div>
            </div>
            <div className="w-full bg-teal-200 dark:bg-teal-800/30 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${fiberProgress}%` }}
                transition={{ duration: 0.8, delay: animationDelay + 0.5 }}
                className="bg-gradient-to-r from-teal-500 to-teal-600 h-2 rounded-full"
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
} 